// components/HRDocuments/AutoRuleEditor.tsx
// Расширенный редактор автоправил: триггер + условия + действия + шаблоны + тест
import React, { useState, useMemo } from 'react';
import { X, Plus, Trash2, Zap, Beaker, AlertCircle, CheckCircle, ChevronDown } from 'lucide-react';

// ─── Справочники ───
const EVENTS = [
  { value: 'employee.created', label: '👤 Сотрудник создан', group: 'Сотрудники' },
  { value: 'employee.hired', label: '🎉 Сотрудник принят', group: 'Сотрудники' },
  { value: 'employee.fired', label: '👋 Сотрудник уволен', group: 'Сотрудники' },
  { value: 'employee.status_changed', label: '🔄 Изменён статус сотрудника', group: 'Сотрудники' },
  { value: 'employee.salary_changed', label: '💰 Изменена зарплата', group: 'Сотрудники' },
  { value: 'employee.position_changed', label: '📈 Изменена должность', group: 'Сотрудники' },
  { value: 'employee.department_changed', label: '🏢 Изменён отдел', group: 'Сотрудники' },
  { value: 'candidate.hired', label: '✅ Кандидат принят', group: 'Кандидаты' },
  { value: 'candidate.status_changed', label: '🔄 Статус кандидата', group: 'Кандидаты' },
  { value: 'vacation.request_created', label: '🏖 Заявка на отпуск', group: 'Отпуска' },
  { value: 'vacation.approved', label: '✅ Отпуск одобрен', group: 'Отпуска' },
  { value: 'vacation.rejected', label: '❌ Отпуск отклонён', group: 'Отпуска' },
  { value: 'document.created', label: '📄 Документ создан', group: 'Документы' },
  { value: 'document.signed', label: '✍️ Документ подписан', group: 'Документы' },
  { value: 'document.completed', label: '✅ Документ завершён', group: 'Документы' },
  { value: 'document.rejected', label: '❌ Документ отклонён', group: 'Документы' },
  { value: 'document.overdue', label: '⏰ Документ просрочен', group: 'Документы' },
  { value: 'acquaintance.created', label: '📋 Ознакомление создано', group: 'Ознакомления' },
  { value: 'acquaintance.completed', label: '✅ Ознакомление завершено', group: 'Ознакомления' },
  { value: 'request.created', label: '📝 Запрос справки создан', group: 'Справки' },
  { value: 'request.ready', label: '✅ Справка готова', group: 'Справки' },
  { value: 'onboarding.started', label: '🚀 Онбординг начат', group: 'Онбординг' },
  { value: 'onboarding.completed', label: '🎉 Онбординг завершён', group: 'Онбординг' },
  { value: 'contract.expiring_soon', label: '⚠️ Договор истекает', group: 'Уведомления' },
  { value: 'probation.ending_soon', label: '⏳ Испытательный срок', group: 'Уведомления' },
  { value: 'birthday.upcoming', label: '🎂 Скоро ДР', group: 'Уведомления' },
  { value: 'work_anniversary.upcoming', label: '🏆 Годовщина', group: 'Уведомления' },
];

const ACTIONS = [
  { value: 'send_notification', label: '🔔 Уведомление', fields: ['title', 'text', 'roles'] },
  { value: 'send_email', label: '📧 Email', fields: ['to', 'subject', 'body'] },
  { value: 'create_reminder', label: '⏰ Напоминание', fields: ['title', 'description', 'dueInDays', 'assigneeRole'] },
  { value: 'create_task', label: '✅ Задача', fields: ['title', 'description', 'assigneeRole'] },
  { value: 'generate_document', label: '📄 Создать документ', fields: ['templateCode'] },
  { value: 'create_onboarding', label: '🚀 Онбординг', fields: ['templateCode'] },
  { value: 'start_workflow', label: '⚙️ Запустить процесс', fields: ['processKey'] },
  { value: 'update_field', label: '✏️ Обновить поле', fields: ['field', 'value'] },
  { value: 'send_telegram', label: '✈️ Telegram', fields: ['chatId', 'text'] },
  { value: 'webhook', label: '🌐 Webhook', fields: ['url', 'method', 'secret'] },
  { value: 'assign_role', label: '🎭 Назначить роль', fields: ['role'] },
  { value: 'add_to_checklist', label: '📋 В чеклист', fields: ['title', 'description'] },
];

const CONDITION_FIELDS = [
  { value: 'employee.position', label: 'Должность сотрудника' },
  { value: 'employee.department', label: 'Отдел' },
  { value: 'employee.status', label: 'Статус' },
  { value: 'document.category', label: 'Категория документа' },
  { value: 'document.status', label: 'Статус документа' },
  { value: 'vacation.days', label: 'Дней отпуска' },
  { value: 'candidate.source', label: 'Источник кандидата' },
];

const OPERATORS = [
  { value: 'eq', label: '= равно' },
  { value: 'ne', label: '≠ не равно' },
  { value: 'contains', label: '⊃ содержит' },
  { value: 'in', label: '∈ один из' },
  { value: 'gt', label: '> больше' },
  { value: 'gte', label: '≥ больше или равно' },
  { value: 'lt', label: '< меньше' },
  { value: 'lte', label: '≤ меньше или равно' },
];

// ─── Шаблоны правил ───
const TEMPLATES = [
  {
    id: 'welcome_email',
    name: '👋 Welcome-письмо',
    description: 'Отправить приветствие новому сотруднику',
    rule: {
      name: 'Приветствие новому сотруднику',
      description: 'Автоматически отправляет welcome-письмо',
      active: true,
      trigger: { event: 'employee.hired', delayDays: 0 },
      actions: [{
        type: 'send_email',
        params: { to: '{employee.email}', subject: 'Добро пожаловать!', body: '<p>Здравствуйте, {employee.firstName}!</p>' },
        order: 1,
      }],
    },
  },
  {
    id: 'hr_reminder',
    name: '⏰ Напоминание HR',
    description: 'Создать задачу при создании документа',
    rule: {
      name: 'Напомнить HR о подписании',
      description: 'Создаёт напоминание при создании документа',
      active: true,
      trigger: { event: 'document.created', delayDays: 0 },
      actions: [{
        type: 'create_reminder',
        params: { title: 'Подписать {document.documentNumber}', dueInDays: 2, assigneeRole: 'hr_manager', priority: 'high' },
        order: 1,
      }],
    },
  },
  {
    id: 'birthday',
    name: '🎂 Уведомление о ДР',
    description: 'Уведомить HR за 3 дня до дня рождения',
    rule: {
      name: 'Скоро день рождения',
      description: 'Уведомление за 3 дня до ДР',
      active: true,
      trigger: { event: 'birthday.upcoming', delayDays: 0 },
      actions: [{
        type: 'send_notification',
        params: { title: '🎂 ДР через {daysUntil} дней', text: '{employee.firstName} {employee.lastName} — {daysUntil} дней до ДР', roles: ['hr_manager', 'admin'] },
        order: 1,
      }],
    },
  },
  {
    id: 'vacation_notify',
    name: '🏖 Заявка на отпуск',
    description: 'Уведомить руководителя о заявке',
    rule: {
      name: 'Новая заявка на отпуск',
      description: 'Уведомить при создании заявки',
      active: true,
      trigger: { event: 'vacation.request_created', delayDays: 0 },
      actions: [{
        type: 'send_notification',
        params: { title: '🏖 Заявка от {employee.firstName}', text: 'Заявка на отпуск', roles: ['admin', 'hr_manager'] },
        order: 1,
      }],
    },
  },
  {
    id: 'onboarding',
    name: '🚀 Авто-онбординг',
    description: 'Запуск онбординга при найме',
    rule: {
      name: 'Запустить онбординг',
      description: 'Автоматически при найме',
      active: true,
      trigger: { event: 'employee.hired', delayDays: 0 },
      actions: [{ type: 'create_onboarding', params: { templateCode: 'default', autoAssign: true }, order: 1 }],
    },
  },
  {
    id: 'contract_alert',
    name: '⚠️ Истекает договор',
    description: 'Уведомить за 14 дней до окончания',
    rule: {
      name: 'Договор истекает',
      description: 'Уведомление за 14 дней',
      active: true,
      trigger: { event: 'contract.expiring_soon', delayDays: 0 },
      actions: [{
        type: 'send_notification',
        params: { title: '⚠️ Договор истекает', text: 'У {employee.firstName} {employee.lastName} истекает договор', roles: ['admin', 'hr_manager'] },
        order: 1,
      }],
    },
  },
];

// ─── Общие типы ───
interface Condition {
  field: string;
  operator: string;
  value: any;
}

interface RuleAction {
  type: string;
  params: Record<string, any>;
  order?: number;
}

interface AutoRule {
  name: string;
  description?: string;
  active: boolean;
  trigger: {
    event: string;
    delayDays?: number;
    condition?: Condition | null;
  };
  actions: RuleAction[];
}

interface Props {
  initialRule?: AutoRule | null;
  onSave: (rule: AutoRule) => Promise<void>;
  onClose: () => void;
  apiPost: (url: string, data: any) => Promise<any>;
}

// ═══════════════════════════════════════════════════════════════
//  ОСНОВНОЙ КОМПОНЕНТ
// ═══════════════════════════════════════════════════════════════
const AutoRuleEditor: React.FC<Props> = ({ initialRule, onSave, onClose, apiPost }) => {
  const [rule, setRule] = useState<AutoRule>(() => initialRule || {
    name: '',
    description: '',
    active: true,
    trigger: { event: 'employee.hired', delayDays: 0, condition: null },
    actions: [{ type: 'send_notification', params: {}, order: 1 }],
  });

  const [showTemplates, setShowTemplates] = useState(!initialRule);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [creating, setCreating] = useState(false);

  // ─── Обновление полей ───
  const upd = (patch: Partial<AutoRule>) => setRule(prev => ({ ...prev, ...patch }));
  const updTrigger = (patch: any) => setRule(prev => ({ ...prev, trigger: { ...prev.trigger, ...patch } }));

  const setCondition = (cond: Condition | null) => updTrigger({ condition: cond });

  const addAction = () => {
    setRule(prev => ({
      ...prev,
      actions: [...prev.actions, { type: 'send_notification', params: {}, order: prev.actions.length + 1 }],
    }));
  };

  const updAction = (idx: number, patch: Partial<RuleAction>) => {
    setRule(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => i === idx ? { ...a, ...patch } : a),
    }));
  };

  const updActionParam = (idx: number, key: string, value: any) => {
    setRule(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => i === idx ? { ...a, params: { ...a.params, [key]: value } } : a),
    }));
  };

  const removeAction = (idx: number) => {
    setRule(prev => ({ ...prev, actions: prev.actions.filter((_, i) => i !== idx) }));
  };

  // ─── Применить шаблон ───
  const applyTemplate = (tpl: typeof TEMPLATES[0]) => {
    setRule(tpl.rule as AutoRule);
    setShowTemplates(false);
    setTestResult(null);
  };

  // ─── Тестировать ───
  const testRule = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await apiPost('/hr/documents/auto-rules/test', { rule });
      setTestResult(r.data);
    } catch (e: any) {
      setTestResult({
        ok: false,
        error: e.response?.data?.error || e.message,
      });
    } finally {
      setTesting(false);
    }
  };

  // ─── Сохранить ───
  const submit = async () => {
    if (!rule.name.trim()) return;
    setCreating(true);
    try {
      await onSave(rule);
    } finally {
      setCreating(false);
    }
  };

  const groupedEvents = useMemo(() => {
    const map: Record<string, typeof EVENTS> = {};
    EVENTS.forEach(e => {
      if (!map[e.group]) map[e.group] = [];
      map[e.group].push(e);
    });
    return map;
  }, []);

  // ═══════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════
  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #8b5cf6, #6a11cb)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <Zap size={18} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>
                {initialRule ? 'Редактировать правило' : 'Новое автоправило'}
              </h2>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                Когда событие → проверить условия → выполнить действия
              </div>
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: 'auto', maxHeight: '70vh', display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* ─── Выбор шаблона ─── */}
          {showTemplates && (
            <div style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(37,117,252,0.06))', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 14, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e' }}>🚀 Быстрый старт — выбери шаблон:</div>
                <button onClick={() => setShowTemplates(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 12, fontWeight: 600 }}>Пропустить →</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {TEMPLATES.map(t => (
                  <button key={t.id} onClick={() => applyTemplate(t)} style={tplBtn}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 2 }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: '#64748b', textAlign: 'left' }}>{t.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ─── Основное ─── */}
          <Section title="📝 Основное" color="#8b5cf6">
            <Field label="Название *">
              <input value={rule.name} onChange={e => upd({ name: e.target.value })} style={input} placeholder="Например: Welcome-письмо новому сотруднику" />
            </Field>
            <Field label="Описание">
              <textarea value={rule.description || ''} onChange={e => upd({ description: e.target.value })} rows={2} style={{ ...input, resize: 'vertical' }} placeholder="Что делает это правило" />
            </Field>
          </Section>

          {/* ─── Триггер ─── */}
          <Section title="⚡ Когда (событие)" color="#f59e0b">
            <Field label="Событие">
              <select value={rule.trigger.event} onChange={e => updTrigger({ event: e.target.value })} style={input}>
                {Object.entries(groupedEvents).map(([group, evs]) => (
                  <optgroup key={group} label={group}>
                    {evs.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>

            <Field label="Задержка (дней) — опционально">
              <input type="number" min={0} value={rule.trigger.delayDays || 0} onChange={e => updTrigger({ delayDays: parseInt(e.target.value) || 0 })} style={input} placeholder="0 — сразу" />
            </Field>
          </Section>

          {/* ─── Условия ─── */}
          <Section title="🔍 Условие (опционально)" color="#0ea5e9">
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
              Правило сработает только если поле соответствует значению
            </div>

            {rule.trigger.condition ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <select value={rule.trigger.condition.field} onChange={e => setCondition({ ...rule.trigger.condition!, field: e.target.value })} style={{ ...input, flex: 1 }}>
                  {CONDITION_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <select value={rule.trigger.condition.operator} onChange={e => setCondition({ ...rule.trigger.condition!, operator: e.target.value })} style={{ ...input, flex: 1 }}>
                  {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <input value={rule.trigger.condition.value} onChange={e => setCondition({ ...rule.trigger.condition!, value: e.target.value })} style={{ ...input, flex: 1 }} placeholder="Значение" />
                <button onClick={() => setCondition(null)} style={iconBtn('#ef4444')} title="Удалить условие">
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <button onClick={() => setCondition({ field: 'employee.department', operator: 'eq', value: '' })} style={addBtn}>
                <Plus size={14} /> Добавить условие
              </button>
            )}
          </Section>

          {/* ─── Действия ─── */}
          <Section title={`🎯 Что делать (${rule.actions.length})`} color="#10b981">
            {rule.actions.map((action, idx) => {
              const actionDef = ACTIONS.find(a => a.value === action.type);
              return (
                <div key={idx} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#64748b', background: '#f1f5f9', padding: '4px 8px', borderRadius: 6 }}>#{idx + 1}</span>
                    <select value={action.type} onChange={e => updAction(idx, { type: e.target.value, params: {} })} style={{ ...input, flex: 1 }}>
                      {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                    {rule.actions.length > 1 && (
                      <button onClick={() => removeAction(idx)} style={iconBtn('#ef4444')} title="Удалить действие">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {actionDef?.fields.map(f => (
                    <div key={f} style={{ marginTop: 6 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 3 }}>
                        {paramLabel(action.type, f)}
                      </label>
                      {paramType(action.type, f) === 'textarea' ? (
                        <textarea value={action.params[f] || ''} onChange={e => updActionParam(idx, f, e.target.value)} rows={2} style={{ ...input, resize: 'vertical', fontSize: 12 }} placeholder={paramPlaceholder(action.type, f)} />
                      ) : paramType(action.type, f) === 'number' ? (
                        <input type="number" value={action.params[f] || ''} onChange={e => updActionParam(idx, f, parseInt(e.target.value) || 0)} style={input} placeholder={paramPlaceholder(action.type, f)} />
                      ) : (
                        <input value={action.params[f] || ''} onChange={e => updActionParam(idx, f, e.target.value)} style={input} placeholder={paramPlaceholder(action.type, f)} />
                      )}
                    </div>
                  ))}

                  {/* Подсказка про переменные */}
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8, padding: 6, background: '#f8fafc', borderRadius: 6 }}>
                    💡 Переменные: <code>{'{employee.firstName}'}</code>, <code>{'{employee.lastName}'}</code>, <code>{'{employee.email}'}</code>, <code>{'{document.documentNumber}'}</code>, <code>{'{daysUntil}'}</code>
                  </div>
                </div>
              );
            })}

            <button onClick={addAction} style={addBtn}>
              <Plus size={14} /> Добавить действие
            </button>
          </Section>

          {/* ─── Тест ─── */}
          <Section title="🧪 Тестирование" color="#f97316">
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
              Проверить правило без сохранения — выполнится dry-run с тестовым сотрудником
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={testRule} disabled={testing} style={{ ...btnWarning, opacity: testing ? 0.6 : 1 }}>
                <Beaker size={14} /> {testing ? 'Тестирование...' : 'Протестировать'}
              </button>
            </div>

            {testResult && (
              <div style={{ marginTop: 12, borderRadius: 12, overflow: 'hidden', border: `1px solid ${testResult.ok ? '#86efac' : '#fca5a5'}` }}>
                <div style={{
                  padding: '10px 14px',
                  background: testResult.ok ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13, fontWeight: 700,
                  color: testResult.ok ? '#047857' : '#b91c1c',
                }}>
                  {testResult.ok ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                  {testResult.ok
                    ? `Тест пройден: ${testResult.summary?.actionsSuccess || 0}/${testResult.summary?.actionsTotal || 0} действий успешно`
                    : 'Ошибка тестирования'}
                </div>
                {testResult.results && (
                  <div style={{ padding: 12, background: '#fff' }}>
                    {testResult.results.map((r: any, i: number) => (
                      <div key={i} style={{ fontSize: 12, padding: '6px 0', borderBottom: i < testResult.results.length - 1 ? '1px dashed #e2e8f0' : 'none', display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ color: r.success ? '#10b981' : '#ef4444', fontWeight: 700 }}>{r.success ? '✓' : '✗'}</span>
                        <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{r.action}</span>
                        {r.error && <span style={{ color: '#ef4444', fontSize: 11 }}>— {r.error}</span>}
                        {!r.error && r.durationMs != null && <span style={{ color: '#94a3b8', fontSize: 11 }}>{r.durationMs}мс</span>}
                      </div>
                    ))}
                    {testResult.context && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
                        <b>Контекст:</b> {testResult.context.employee?.name || '—'} ({testResult.context.employee?.email || '—'})
                      </div>
                    )}
                  </div>
                )}
                {testResult.error && (
                  <div style={{ padding: 12, background: '#fff', fontSize: 12, color: '#b91c1c' }}>
                    {testResult.error}
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* ─── Активность ─── */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={rule.active} onChange={e => upd({ active: e.target.checked })} style={{ width: 18, height: 18, cursor: 'pointer' }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>
              ✅ Правило активно
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginTop: 2 }}>
                Снимай галку, чтобы временно отключить правило
              </div>
            </div>
          </label>

        </div>

        {/* Footer */}
        <div style={footer}>
          <button onClick={() => setShowTemplates(!showTemplates)} style={btnOutline}>
            {showTemplates ? 'Скрыть шаблоны' : '📋 Показать шаблоны'}
          </button>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btnOutline}>Отмена</button>
          <button onClick={submit} disabled={creating || !rule.name.trim()} style={{ ...btnPrimary, opacity: (creating || !rule.name.trim()) ? 0.5 : 1 }}>
            {creating ? 'Сохранение...' : initialRule ? '💾 Сохранить' : '⚡ Создать правило'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Вспомогательные компоненты ───
const Section: React.FC<{ title: string; color: string; children: React.ReactNode }> = ({ title, color, children }) => (
  <div style={{ background: '#f8fafc', border: `1px solid ${color}20`, borderRadius: 14, padding: 16 }}>
    <div style={{ fontSize: 13, fontWeight: 800, color, marginBottom: 12 }}>{title}</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

// ─── Labels/placeholders для полей действий ───
function paramLabel(actionType: string, field: string): string {
  const labels: Record<string, string> = {
    title: 'Заголовок', text: 'Текст', roles: 'Роли (через запятую)',
    to: 'Кому (email)', subject: 'Тема', body: 'Текст письма',
    dueInDays: 'Срок (дней)', assigneeRole: 'Роль исполнителя',
    description: 'Описание', priority: 'Приоритет',
    templateCode: 'Код шаблона', processKey: 'Ключ процесса',
    field: 'Поле', value: 'Значение',
    chatId: 'Chat ID', url: 'URL', method: 'Метод (POST/GET)', secret: 'Секрет',
    role: 'Роль',
  };
  return labels[field] || field;
}

function paramPlaceholder(actionType: string, field: string): string {
  const ph: Record<string, string> = {
    title: 'Уведомление HR',
    text: 'Текст уведомления',
    roles: 'hr_manager, admin',
    to: '{employee.email}',
    subject: 'Добро пожаловать!',
    body: '<p>Привет, {employee.firstName}!</p>',
    dueInDays: '2',
    assigneeRole: 'hr_manager',
    templateCode: 'T-1',
    processKey: 'onboarding_v2',
    field: 'employee.status',
    value: 'active',
    chatId: '123456789',
    url: 'https://n8n.example.com/webhook/xxx',
    method: 'POST',
    secret: 'my-secret-key',
    role: 'employee',
  };
  return ph[field] || '';
}

function paramType(actionType: string, field: string): string {
  if (field === 'body' || field === 'text' || field === 'description') return 'textarea';
  if (field === 'dueInDays') return 'number';
  return 'text';
}

// ─── Стили ───
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 20 };
const modal: React.CSSProperties = { background: '#fff', borderRadius: 20, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(15,23,42,0.3)', overflow: 'hidden' };
const header: React.CSSProperties = { padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const footer: React.CSSProperties = { padding: '14px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, alignItems: 'center', background: '#f8fafc' };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const btnPrimary: React.CSSProperties = { background: 'linear-gradient(135deg, #8b5cf6, #6a11cb)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnOutline: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#475569', fontFamily: 'inherit' };
const btnWarning: React.CSSProperties = { background: 'linear-gradient(135deg, #f97316, #ea580c)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 };
const addBtn: React.CSSProperties = { background: 'rgba(16,185,129,0.08)', border: '1px dashed rgba(16,185,129,0.4)', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', color: '#10b981', fontSize: 12, fontWeight: 700, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 };
const tplBtn: React.CSSProperties = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' };
const iconBtn = (color: string): React.CSSProperties => ({ background: `${color}15`, border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color, flexShrink: 0 });

export default AutoRuleEditor;
